import React from 'react';
import './ResultsTable.css';

function ResultsTable({ results, stats }) {
  if (results.length === 0) {
    return null;
  }

  // Count new vs duplicate businesses
  const newCount = results.filter(r => !r.is_duplicate).length;
  const duplicateCount = results.filter(r => r.is_duplicate).length;
  const suiteCrmUrl = process.env.REACT_APP_SUITECRM_URL;
  const savedSearchPrefix =
    process.env.REACT_APP_SAVED_SEARCH_PREFIX || 'Leads - ';

  const verticalSummary = results.reduce((acc, listing) => {
    const vertical =
      listing.business_vertical || listing.businessVertical || 'Unknown';
    if (!acc[vertical]) {
      acc[vertical] = { total: 0, new: 0, duplicates: 0 };
    }
    acc[vertical].total += 1;
    if (listing.is_duplicate) {
      acc[vertical].duplicates += 1;
    } else {
      acc[vertical].new += 1;
    }
    return acc;
  }, {});

  return (
    <div className="results-container">
      <div className="results-header">
        <h2>Scraped Results ({results.length} businesses)</h2>
        {stats && (
          <div className="results-stats">
            <span className="stat-badge stat-new">
              ✓ {stats.new || newCount} New
            </span>
            {(stats.duplicates > 0 || duplicateCount > 0) && (
              <span className="stat-badge stat-duplicate">
                ⚠ {stats.duplicates || duplicateCount} Already in Database
              </span>
            )}
          </div>
        )}
      </div>

      {Object.keys(verticalSummary).length > 0 && (
        <div className="saved-view-shortcuts">
          <div className="shortcut-header">
            <h3>SuiteCRM Saved Views</h3>
            {!suiteCrmUrl && (
              <span className="helper-text">
                Set <code>REACT_APP_SUITECRM_URL</code> to enable deep links.
              </span>
            )}
          </div>
          <div className="shortcut-grid">
            {Object.entries(verticalSummary).map(([vertical, data]) => {
              const savedViewName = `${savedSearchPrefix}${vertical}`;
              const link = suiteCrmUrl
                ? `${suiteCrmUrl}/index.php?module=Leads&action=index&saved_search_select=${encodeURIComponent(
                    savedViewName
                  )}`
                : null;
              return (
                <div key={vertical} className="shortcut-card">
                  <div className="shortcut-metric">
                    <span className="metric-label">{vertical}</span>
                    <span className="metric-value">{data.total} leads</span>
                  </div>
                  <div className="metric-split">
                    <span className="metric-new">{data.new} new</span>
                    <span className="metric-dup">
                      {data.duplicates} duplicates
                    </span>
                  </div>
                  {link ? (
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shortcut-link"
                    >
                      Open &ldquo;{savedViewName}&rdquo;
                    </a>
                  ) : (
                    <code className="shortcut-link code">
                      {savedViewName}
                    </code>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="table-wrapper">
        <table className="results-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Business Name</th>
              <th>Full Address</th>
              <th>Street</th>
              <th>City</th>
              <th>State</th>
              <th>Zip</th>
              <th>Phone</th>
              <th>Business Hours</th>
              <th>Business Vertical</th>
              <th>Website</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {results.map((result, index) => (
              <tr 
                key={index}
                className={result.is_duplicate ? 'duplicate-row' : 'new-row'}
                title={result.is_duplicate ? 'Already exists in database' : 'New business'}
              >
                <td className="status-cell">
                  {result.is_duplicate ? (
                    <span className="status-badge duplicate" title="Already in database">
                      Existing
                    </span>
                  ) : (
                    <span className="status-badge new" title="New business">
                      New
                    </span>
                  )}
                </td>
                <td>{result.name || '-'}</td>
                <td>{result.address || '-'}</td>
                <td>{result.street || '-'}</td>
                <td>{result.city || '-'}</td>
                <td>{result.state || '-'}</td>
                <td>{result.zip || '-'}</td>
                <td>{result.phone || '-'}</td>
                <td className="hours-cell">{result.hours || '-'}</td>
                <td>{result.business_vertical || result.businessVertical || 'Unknown'}</td>
                <td>
                  {result.website ? (
                    <a 
                      href={result.website.startsWith('http') ? result.website : `https://${result.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {result.website}
                    </a>
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  {result.email ? (
                    <a href={`mailto:${result.email}`}>{result.email}</a>
                  ) : (
                    '-'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ResultsTable;

